"use client";

import { useEffect, useState, type FormEvent } from "react";
import Link from "next/link";
import { useI18n } from "@/i18n/context";
import { useCaptcha } from "@/components/useCaptcha";
import { CaptchaWidget } from "@/components/CaptchaWidget";
import "../legal.css";

const REQUEST_TYPES: { value: string; en: string; fr: string }[] = [
  { value: "account_deletion", en: "Delete my account", fr: "Suppression de mon compte" },
  { value: "profile_deletion", en: "Delete my profile", fr: "Suppression de mon profil" },
  { value: "data_access", en: "Access / recover my data", fr: "Accès / récupération de mes données" },
  { value: "profiling_objection", en: "Object to profiling", fr: "Opposition au profilage" },
  { value: "other", en: "Other request", fr: "Autre demande" },
];

export function SuppressionForm() {
  const { locale, toggleLocale } = useI18n();
  const captcha = useCaptcha();
  const [status, setStatus] = useState<"idle" | "sending" | "sent" | "error">("idle");

  const tp = (en: string, fr: string) => (locale === "fr" ? fr : en);

  // Keep <html lang> in sync so the page reads correctly in the app WebView too.
  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);

  const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (status === "sending") return;
    const form = e.currentTarget;
    const fd = new FormData(form);
    setStatus("sending");
    try {
      const altcha = await captcha.ensureToken();
      const res = await fetch("/api/data-request", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: String(fd.get("email") ?? ""),
          requestType: String(fd.get("requestType") ?? ""),
          message: String(fd.get("message") ?? ""),
          locale,
          source: "web",
          altcha,
        }),
      });
      if (!res.ok) throw new Error(String(res.status));
      setStatus("sent");
      form.reset();
      captcha.reset();
    } catch {
      setStatus("error");
      captcha.reset();
    }
  };

  return (
    <div className="legal-scope">
      <nav className="top">
        <Link href="/" className="wordmark" aria-label="WooZoo home">
          <span className="symbol" aria-hidden="true" />
          <span className="wm">WooZoo</span>
        </Link>
        <div className="center-label">
          {tp("Data request", "Demande relative aux données")}
        </div>
        <div className="right-cluster">
          <button className="lang" type="button" onClick={() => toggleLocale()} aria-label="Toggle language">
            <span data-lang="en" className={locale === "en" ? "active" : ""}>EN</span>
            <span className="sep">/</span>
            <span data-lang="fr" className={locale === "fr" ? "active" : ""}>FR</span>
          </button>
          <Link href="/" className="back-home">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6" /></svg>
            <span className="bh-label">{tp("Back to site", "Retour au site")}</span>
          </Link>
        </div>
      </nav>

      <header className="page-head">
        <div className="eyebrow">{tp("Your rights", "Vos droits")}</div>
        <h1 className="display">
          {tp("Delete or recover your data.", "Supprimer ou récupérer vos données.")}
        </h1>
        <p className="lede">
          {tp(
            "Exercise your GDPR rights: ask us to delete your account or profile, recover a copy of your data, or object to profiling. We reply within one month.",
            "Exercez vos droits RGPD : demandez la suppression de votre compte ou de votre profil, la récupération d'une copie de vos données, ou l'opposition au profilage. Nous répondons sous un mois.",
          )}
        </p>
      </header>

      <main>
        <form className="req-form" onSubmit={onSubmit}>
          <div className="req-field">
            <label htmlFor="d-type">{tp("Request type", "Type de demande")}</label>
            <select id="d-type" name="requestType" defaultValue="account_deletion">
              {REQUEST_TYPES.map((r) => (
                <option key={r.value} value={r.value}>
                  {tp(r.en, r.fr)}
                </option>
              ))}
            </select>
          </div>

          <div className="req-field">
            <label htmlFor="d-email">{tp("Your email", "Votre email")}</label>
            <input type="email" id="d-email" name="email" required
              placeholder={tp("the email tied to your account / profile", "l'email lié à votre compte / profil")} />
            <p className="req-help">
              {tp(
                "Use the email associated with your WooZoo account or profile so we can verify your request.",
                "Utilisez l'email associé à votre compte ou profil WooZoo afin que nous puissions vérifier votre demande.",
              )}
            </p>
          </div>

          <div className="req-field">
            <label htmlFor="d-msg">{tp("Message (optional)", "Message (facultatif)")}</label>
            <textarea id="d-msg" name="message"
              placeholder={tp("Any detail that helps us handle your request.", "Tout détail utile au traitement de votre demande.")} />
          </div>

          <div className="req-actions">
            <div style={{ marginBottom: 4 }}>
              <CaptchaWidget status={captcha.status} onRetry={captcha.reset} />
            </div>
            <button type="submit" disabled={status === "sending"}>
              {status === "sending"
                ? tp("Sending…", "Envoi…")
                : tp("Send my request", "Envoyer ma demande")}
            </button>
            {status === "sent" && (
              <p className="form-note ok">
                {tp("Request received. We'll get back to you within one month.", "Demande reçue. Nous revenons vers vous sous un mois.")}
              </p>
            )}
            {status === "error" && (
              <p className="form-note err">
                {tp("Something went wrong. Please try again.", "Une erreur est survenue. Réessayez.")}
              </p>
            )}
            <p className="form-note hint">
              {tp("You can also email dpo@woozoo.io", "Vous pouvez aussi écrire à dpo@woozoo.io")}
            </p>
          </div>
        </form>
      </main>
    </div>
  );
}
